import React, { useState, useEffect, useRef } from 'react';
import { Trash2, ChevronDown, Link2, Copy, AlertCircle, ChevronsDownUp, ChevronsUpDown, X, Tag, Lock, Layers } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { computeLine, filsToAed } from '../../lib/engine';
import { useMoney } from '../../context/CurrencyContext';
import { cn } from '../../lib/utils';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '../ui/command';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { NumberStepper } from '../NumberStepper';

const CAT_MASTER = {
  MATERIAL: 'material', VEHICLE: 'vehicle', MANPOWER: 'manpower', SUPERVISORS: 'supervisors',
  'TOOLS & EQUIPMENT': 'tools', SAFETY: 'safety', SUBCONTRACTOR: 'subcontractor', MISC: 'misc',
};

// Auto-growing, wrapping textarea so long HV specs are never truncated (Part B).
function AutoTextarea({ value, onChange, placeholder, className, disabled }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; }
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      title={value || placeholder}
      className={cn('grid-input resize-none overflow-hidden whitespace-pre-wrap break-words leading-snug', className)}
    />
  );
}

// Brand selector for material lines that expose brand options (Part A).
function BrandPicker({ brands, value, onPick }) {
  const [open, setOpen] = useState(false);
  const active = brands.filter((b) => b.active !== false);
  const sel = brands.find((b) => b.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="brand-picker-trigger"
          className={cn('mt-1 flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors hover:bg-muted',
            sel ? 'border-[hsl(var(--copper)/0.5)] text-[hsl(var(--copper))]' : 'text-muted-foreground')}
          title="Select a brand — its rate overrides the base material rate">
          <Tag size={10} />{sel ? sel.name : 'Base rate'}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-0">
        <Command>
          <CommandList>
            <CommandEmpty>No brands.</CommandEmpty>
            <CommandItem value="base-rate" onSelect={() => { onPick(null); setOpen(false); }}>
              <span className="text-xs">Base rate</span>
              <span className="ml-auto text-[10px] text-muted-foreground">default</span>
            </CommandItem>
            {active.map((b) => (
              <CommandItem key={b.id} value={`${b.name} ${b.remark || ''}`} onSelect={() => { onPick(b.id); setOpen(false); }}>
                <span className={cn('text-xs', b.id === value && 'font-semibold text-[hsl(var(--copper))]')}>{b.name}</span>
                {b.remark ? <span className="ml-2 truncate text-[10px] text-muted-foreground">{b.remark}</span> : null}
                <span className="ml-auto font-num text-[10px]">{Number(b.rate || 0).toLocaleString()}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CodePicker({ category, masters, onPick }) {
  const mtype = CAT_MASTER[category];
  const list = (mtype ? Object.values(masters[mtype] || {}) : []).filter((m) => m.active !== false);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="grid h-7 w-7 place-items-center rounded border text-muted-foreground hover:bg-muted"
          title="Link to a rate-master code (auto-fills rate)"><Link2 size={12} /></button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[34rem] max-w-[90vw] p-0">
        <Command>
          <CommandInput placeholder="Search code or description…" />
          <CommandList>
            <CommandEmpty>No codes.</CommandEmpty>
            {list.map((m) => {
              const brandNames = (m.brands || []).map((b) => b.name).filter(Boolean).join(' ');
              return (
                <CommandItem
                  key={m.code}
                  value={`${m.code} ${m.description || m.trade || ''} ${brandNames}`}
                  onSelect={() => onPick(m.code)}
                  className="items-start gap-2"
                >
                  <span className="mt-0.5 shrink-0 font-num text-xs text-[hsl(var(--copper))]">{m.code}</span>
                  <span className="min-w-0 flex-1 whitespace-normal break-words text-xs text-muted-foreground">{m.description || m.trade}</span>
                  <span className="mt-0.5 shrink-0 font-num text-[10px] tabular-nums">{m.rate}</span>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// K factor (MATERIAL lines only) — a compact two-way cell: K Value (real current
// market price) and K % (spread over the master rate). Editing either drives the
// other. Local state preserves in-progress typing (e.g. decimals); it resyncs from
// props when the line or its master rate changes.
function KFactorCell({ lineId, kValueRaw, masterRateFils, disabled, active, onChange }) {
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const masterAed = filsToAed(masterRateFils);
  const hasK = kValueRaw !== null && kValueRaw !== undefined && kValueRaw !== '';
  const [valStr, setValStr] = useState(String(round2(hasK ? Number(kValueRaw) : masterAed)));
  const [pctStr, setPctStr] = useState('0');

  useEffect(() => {
    const has = kValueRaw !== null && kValueRaw !== undefined && kValueRaw !== '';
    const v = has ? Number(kValueRaw) : masterAed;
    setValStr(String(round2(v)));
    setPctStr(String(round2(masterAed ? ((v - masterAed) / masterAed) * 100 : 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId, masterRateFils]);

  const commitValue = (s) => {
    setValStr(s);
    if (s === '') { onChange(null); setPctStr('0'); return; }
    const n = Number(s);
    if (Number.isNaN(n)) return;
    const v = Math.max(0, n);
    onChange(v);
    setPctStr(String(round2(masterAed ? ((v - masterAed) / masterAed) * 100 : 0)));
  };
  const commitPct = (s) => {
    setPctStr(s);
    if (s === '') { onChange(null); setValStr(String(round2(masterAed))); return; }
    const p = Number(s);
    if (Number.isNaN(p)) return;
    const v = round2(Math.max(0, masterAed * (1 + p / 100)));
    onChange(v);
    setValStr(String(v));
  };

  const cls = cn('grid-input text-right font-num', active && hasK && 'font-semibold text-[hsl(var(--copper))]');
  return (
    <>
      <td className="px-1 py-1">
        <input type="number" min="0" step="any" value={valStr} disabled={disabled}
          data-testid={`cost-kvalue-${lineId}`} className={cn(cls, 'w-20')} placeholder={String(round2(masterAed))}
          onChange={(e) => commitValue(e.target.value)} title="Real current market price (K value)" />
      </td>
      <td className="px-1 py-1">
        <input type="number" step="any" value={pctStr} disabled={disabled}
          data-testid={`cost-kpct-${lineId}`} className={cn(cls, 'w-12')} placeholder="0"
          onChange={(e) => commitPct(e.target.value)} title="K spread over master rate (%)" />
      </td>
    </>
  );
}


// Picker to apply an Activity Master template to the selected sale line's Cost BOQ.
function ApplyActivityButton({ activities, onApply }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="apply-activity-btn"
          className="flex items-center gap-1 rounded-lg border border-[hsl(var(--brand)/0.4)] px-2 py-1 text-[11px] font-medium text-[hsl(var(--brand))] hover:bg-[hsl(var(--brand)/0.08)]">
          <Layers size={12} />Apply activity
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[28rem] max-w-[92vw] p-0">
        <Command>
          <CommandInput placeholder="Search activities…" />
          <CommandList>
            <CommandEmpty>No activities. Create them in the Activity Master.</CommandEmpty>
            {activities.map((a) => (
              <CommandItem key={a.id} value={`${a.activityCode} ${a.description}`}
                onSelect={() => { onApply(a); setOpen(false); }} className="items-start gap-2">
                <span className="mt-0.5 shrink-0 font-num text-xs text-[hsl(var(--copper))]">{a.activityCode}</span>
                <span className="min-w-0 flex-1 whitespace-normal break-words text-xs">{a.description}</span>
                <span className="mt-0.5 shrink-0 font-num text-[10px] tabular-nums text-muted-foreground">{(a.composition || []).length} rows</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


export function CostBoqGrid({ saleItem, rawSaleItem = null, masters, wide = false, readOnly = false, projectDirectCost = 0, applyK = false, onToggleApplyK, activities = [], onApplyActivity, onDetachActivity, onChange, onAddLine, onDeleteLine, onDuplicateLine, onPasteRows, onBulkCopy, onBulkDelete }) {
  const { money, code } = useMoney();
  const [collapsed, setCollapsed] = useState({});
  const [selected, setSelected] = useState({}); // lineId -> catName
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [pasteCat, setPasteCat] = useState(null);
  const [pasteText, setPasteText] = useState('');
  const qty = Number(saleItem.qty) || 1;
  const categories = saleItem.costBoq.categories;

  // The grid renders the COMPUTED sale item (for subtotals / effective unit cost),
  // but raw fields (kValue rate, manual rate) live on the DRAFT line — index them by
  // lineId so K maths + inputs read the un-computed values (avoids double-conversion).
  const rawLineById = {};
  for (const c of (rawSaleItem?.costBoq?.categories || [])) {
    for (const l of (c.lines || [])) rawLineById[l.lineId] = l;
  }

  // Distinct Activity Master templates currently applied to this sale line (with row counts).
  const appliedActivities = [];
  {
    const byId = {};
    for (const c of (rawSaleItem?.costBoq?.categories || [])) {
      for (const l of (c.lines || [])) {
        const a = l.activity;
        if (!a || !a.instanceId) continue;
        if (!byId[a.instanceId]) { byId[a.instanceId] = { ...a, count: 0 }; appliedActivities.push(byId[a.instanceId]); }
        byId[a.instanceId].count += 1;
      }
    }
  }

  // Clear selection when switching to a different sale line.
  useEffect(() => { setSelected({}); }, [saleItem.itemId]);

  const toggle = (name) => setCollapsed((c) => ({ ...c, [name]: !c[name] }));
  const allCollapsed = categories.length > 0 && categories.every((c) => collapsed[c.name]);
  const toggleAll = () => {
    if (allCollapsed) setCollapsed({});
    else setCollapsed(Object.fromEntries(categories.map((c) => [c.name, true])));
  };

  const selItems = Object.entries(selected).map(([lineId, catName]) => ({ lineId, catName }));
  const selCount = selItems.length;
  const toggleRow = (lineId, catName) => setSelected((s) => {
    const next = { ...s };
    if (next[lineId]) delete next[lineId]; else next[lineId] = catName;
    return next;
  });
  const toggleSection = (cat, checked) => setSelected((s) => {
    const next = { ...s };
    cat.lines.forEach((l) => { if (checked) next[l.lineId] = cat.name; else delete next[l.lineId]; });
    return next;
  });
  const clearSel = () => setSelected({});
  const doBulkCopy = () => { onBulkCopy(selItems); clearSel(); };
  const doBulkDelete = () => { onBulkDelete(selItems); clearSel(); setBulkDeleteOpen(false); };

  const handlePaste = () => {
    const rows = pasteText.trim().split('\n').map((line) => {
      const cells = line.split('\t').length > 1 ? line.split('\t') : line.split(',');
      const [c = '', description = '', unit = '', qtyPerUnit = '1', rate = '0'] = cells.map((x) => x.trim());
      return { code: c, description, unit, qtyPerUnit: Math.max(0, parseFloat(qtyPerUnit) || 1), rate: Math.max(0, parseFloat(rate) || 0) };
    });
    onPasteRows(pasteCat, rows);
    setPasteCat(null); setPasteText('');
  };

  return (
    <div className="flex h-full flex-col" data-testid="workspace-cost-boq-grid">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-num text-xs font-semibold text-[hsl(var(--copper))]">{saleItem.itemNo}</span>
            <span className="font-display text-sm font-semibold">Cost BOQ</span>
            <button data-testid="cost-boq-collapse-all" onClick={toggleAll}
              className="flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted">
              {allCollapsed ? <ChevronsUpDown size={12} /> : <ChevronsDownUp size={12} />}
              {allCollapsed ? 'Expand all' : 'Collapse all'}
            </button>
          </div>
          <div className="line-clamp-1 max-w-xl text-[11px] text-muted-foreground">{saleItem.description}</div>
        </div>
        <div className="flex shrink-0 items-center gap-4">
          {/* K factor — tender-level Apply-K market-price toggle */}
          <div data-testid="apply-k-toggle-wrap"
            className={cn('flex items-center gap-2 rounded-xl border px-2.5 py-1.5 transition-colors',
              applyK ? 'border-[hsl(var(--copper)/0.5)] bg-[hsl(var(--copper)/0.08)]' : 'bg-muted/30')}>
            <div className="text-right leading-tight">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Apply K</div>
              <div data-testid="apply-k-mode-label" className={cn('text-[10px] font-semibold', applyK ? 'text-[hsl(var(--copper))]' : 'text-muted-foreground')}>
                {applyK ? 'K · real price' : 'Master rates'}
              </div>
            </div>
            <Switch data-testid="apply-k-switch" checked={applyK} disabled={readOnly}
              onCheckedChange={(v) => onToggleApplyK && onToggleApplyK(!!v)} aria-label="Apply K market price" />
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Effective Unit Cost</div>
            <motion.div key={saleItem.effectiveUnitCost} initial={{ scale: 1.08, color: 'hsl(var(--copper))' }} animate={{ scale: 1, color: 'hsl(var(--foreground))' }} transition={{ duration: 0.4 }}
              className="money text-base font-bold">{code} {money(saleItem.effectiveUnitCost)}</motion.div>
          </div>
        </div>
      </div>

      {/* Applied activities bar — apply/detach Activity Master templates */}
      {(appliedActivities.length > 0 || (!readOnly && activities.length > 0)) && (
        <div className="flex flex-wrap items-center gap-2 border-b bg-muted/20 px-4 py-2" data-testid="applied-activities-bar">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Activities</span>
          {appliedActivities.map((a) => (
            <span key={a.instanceId} data-testid={`applied-activity-${a.instanceId}`}
              className="flex items-center gap-1 rounded-lg border border-[hsl(var(--brand)/0.4)] bg-[hsl(var(--brand)/0.08)] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--brand))]">
              <Layers size={10} />{a.code} · {a.name} <span className="text-muted-foreground">({a.count})</span>
              {!readOnly && (
                <button data-testid={`detach-activity-${a.instanceId}`} onClick={() => onDetachActivity && onDetachActivity(a.instanceId)}
                  title="Detach this activity — clears its rows so you can enter them manually"
                  className="ml-0.5 rounded hover:text-[hsl(var(--danger))]"><X size={11} /></button>
              )}
            </span>
          ))}
          {appliedActivities.length === 0 && <span className="text-[10px] text-muted-foreground">None applied</span>}
          {!readOnly && <div className="ml-auto"><ApplyActivityButton activities={activities} onApply={onApplyActivity} /></div>}
        </div>
      )}

      {/* Bulk action bar (A3) */}
      <AnimatePresence>
        {selCount > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            data-testid="cost-boq-bulk-bar"
            className="flex items-center gap-2 overflow-hidden border-b bg-[hsl(var(--primary)/0.08)] px-4 py-2 text-xs">
            <span className="font-medium" data-testid="cost-boq-bulk-count">{selCount} selected</span>
            <Button size="sm" variant="outline" className="h-7" data-testid="cost-boq-bulk-copy" onClick={doBulkCopy}><Copy size={12} className="mr-1.5" />Copy</Button>
            <Button size="sm" variant="outline" className="h-7 text-[hsl(var(--danger))]" data-testid="cost-boq-bulk-delete" onClick={() => setBulkDeleteOpen(true)}><Trash2 size={12} className="mr-1.5" />Delete</Button>
            <button onClick={clearSel} className="ml-auto flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="cost-boq-bulk-clear"><X size={12} />Clear</button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 overflow-y-auto px-2 py-2">
        {categories.map((cat) => {
          const isCollapsed = collapsed[cat.name];
          const sectionLineIds = cat.lines.map((l) => l.lineId);
          const sectionSelectedCount = sectionLineIds.filter((lid) => selected[lid]).length;
          const allSectionSelected = cat.lines.length > 0 && sectionSelectedCount === cat.lines.length;
          const someSectionSelected = sectionSelectedCount > 0 && !allSectionSelected;
          return (
            <div key={cat.name} className="mb-2 overflow-hidden rounded-xl border">
              <button onClick={() => toggle(cat.name)}
                className="flex w-full items-center justify-between bg-muted/40 px-3 py-2 text-left hover:bg-muted/60">
                <div className="flex items-center gap-2">
                  <ChevronDown size={14} className={cn('transition-transform', isCollapsed && '-rotate-90')} />
                  <span className="text-xs font-semibold uppercase tracking-wide">{cat.name}</span>
                  <span className="rounded-full bg-card px-1.5 py-0.5 text-[10px] text-muted-foreground">{cat.lines.length}</span>
                </div>
                <span className="money text-xs font-semibold">{money(cat.subtotal)}</span>
              </button>
              <AnimatePresence initial={false}>
                {!isCollapsed && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className={cn('w-full text-xs', cat.name === 'MATERIAL' ? (wide ? 'min-w-[1000px]' : 'min-w-[820px]') : 'min-w-[720px]')}>
                        <thead><tr className="border-b text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                          <th className="w-9 px-2 py-1.5">
                            <Checkbox checked={allSectionSelected ? true : (someSectionSelected ? 'indeterminate' : false)}
                              onCheckedChange={(v) => toggleSection(cat, !!v)}
                              disabled={!cat.lines.length || readOnly}
                              data-testid={`cost-boq-select-all-${cat.name}`} aria-label={`Select all ${cat.name}`} />
                          </th>
                          <th className="w-8 px-1 py-1.5">#</th>
                          <th className="w-32 px-2 py-1.5">Code</th>
                          <th className={cn('px-2 py-1.5', cat.name === 'MATERIAL'
                            ? (wide ? 'min-w-[240px]' : 'min-w-[120px]')
                            : (wide ? 'min-w-[420px]' : 'min-w-[200px]'))}>Description</th>
                          <th className="w-16 px-2 py-1.5">Unit</th>
                          <th className="w-16 px-2 py-1.5 text-right">Qty/Unit</th>
                          <th className="w-20 px-2 py-1.5 text-right">Rate</th>
                          {cat.name === 'MATERIAL' && <th className="w-20 whitespace-nowrap px-2 py-1.5 text-right" title="Real current market price (K rate)">K Value</th>}
                          {cat.name === 'MATERIAL' && <th className="w-14 whitespace-nowrap px-1 py-1.5 text-right" title="Spread of K value over master rate">K %</th>}
                          <th className="w-24 px-2 py-1.5 text-right">Amount</th>
                          <th className="w-14 px-2 py-1.5" />
                        </tr></thead>
                        <tbody>
                          {cat.lines.map((ln, idx) => {
                            const rawLn = rawLineById[ln.lineId] || ln;
                            const fromActivity = rawLn.activity || null;
                            const locked = !!fromActivity;
                            const cl = computeLine(rawLn, qty, masters, cat.name, applyK);
                            const isSel = !!selected[ln.lineId];
                            const isMat = cat.name === 'MATERIAL';
                            const matItem = cat.name === 'MATERIAL' && ln.code ? masters.material?.[ln.code.toUpperCase()] : null;
                            const brands = matItem?.brands || [];
                            return (
                              <tr key={ln.lineId} data-testid={`cost-line-${ln.lineId}`}
                                className={cn('border-b last:border-0 align-middle hover:bg-muted/30', isSel && 'bg-[hsl(var(--primary)/0.06)]')}>
                                <td className="px-2 py-1">
                                  <Checkbox checked={isSel} onCheckedChange={() => toggleRow(ln.lineId, cat.name)}
                                    disabled={readOnly || locked}
                                    data-testid={`cost-line-select-${ln.lineId}`} aria-label="Select line" />
                                </td>
                                <td className="px-1 py-1 text-center text-muted-foreground">{idx + 1}</td>
                                <td className="px-1 py-1">
                                  <div className="flex items-center gap-1">
                                    {/* P2 — a linked line's Code is read-only (master-owned); manual/unresolved stay editable. */}
                                    {cl.linked ? (
                                      <span data-testid={`cost-code-locked-${ln.lineId}`} title={`${ln.code} — linked to rate master (read-only)`}
                                        className="flex w-28 items-center gap-1 font-num text-xs text-[hsl(var(--copper))]">
                                        <Lock size={10} className="shrink-0 opacity-60" />{ln.code}
                                      </span>
                                    ) : (
                                      <input value={ln.code || ''} title={ln.code || 'No code — manual line'}
                                        disabled={readOnly}
                                        onChange={(e) => { onChange(cat.name, ln.lineId, 'code', e.target.value.toUpperCase()); if (ln.brandId) onChange(cat.name, ln.lineId, 'brandId', null); }}
                                        className={cn('grid-input w-28 font-num', cl.unresolved && 'text-[hsl(var(--danger))]')} placeholder="—" />
                                    )}
                                    {!readOnly && !locked && (cl.linked ? (
                                      <button data-testid={`cost-unlink-${ln.lineId}`} title="Unlink from rate master (convert to a manual, editable line)"
                                        onClick={() => { onChange(cat.name, ln.lineId, 'code', ''); onChange(cat.name, ln.lineId, 'brandId', null); }}
                                        className="grid h-7 w-7 place-items-center rounded border text-muted-foreground hover:bg-muted hover:text-[hsl(var(--danger))]"><X size={12} /></button>
                                    ) : (
                                      <CodePicker category={cat.name} masters={masters} onPick={(c) => { onChange(cat.name, ln.lineId, 'code', c); onChange(cat.name, ln.lineId, 'brandId', null); }} />
                                    ))}
                                  </div>
                                </td>
                                <td className="px-1 py-1">
                                  <div className="flex items-start gap-1">
                                    {cl.unresolved && <AlertCircle size={11} className="mt-1.5 shrink-0 text-[hsl(var(--danger))]" title={`Code ${ln.code} not found in the rate master`} />}
                                    <div className="min-w-0 flex-1">
                                      {/* P2 — linked Description is read-only (shows master text); manual stays editable. */}
                                      {cl.linked ? (
                                        <div data-testid={`cost-desc-locked-${ln.lineId}`} title={cl.description}
                                          className="whitespace-pre-wrap break-words py-1 text-xs leading-snug text-muted-foreground">{cl.description}</div>
                                      ) : (
                                        <AutoTextarea value={ln.description ?? ''} onChange={(e) => onChange(cat.name, ln.lineId, 'description', e.target.value)}
                                          disabled={readOnly}
                                          placeholder={'Description'} />
                                      )}
                                      {!readOnly && !locked && brands.length > 0 && (
                                        <BrandPicker brands={brands} value={ln.brandId || null}
                                          onPick={(bid) => onChange(cat.name, ln.lineId, 'brandId', bid)} />
                                      )}
                                      {fromActivity && (
                                        <div data-testid={`activity-line-badge-${ln.lineId}`}
                                          title="Locked — this row comes from an activity. Edit the composition in the Activity Master."
                                          className="mt-1 inline-flex items-center gap-1 rounded border border-[hsl(var(--brand)/0.4)] bg-[hsl(var(--brand)/0.08)] px-1.5 py-0.5 text-[9px] font-medium text-[hsl(var(--brand))]">
                                          <Layers size={9} />from activity: {fromActivity.name}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-1 py-1">
                                  {/* P2 — linked Unit is read-only (master-owned). */}
                                  {cl.linked ? (
                                    <span data-testid={`cost-unit-locked-${ln.lineId}`} className="block w-16 truncate py-1 text-xs text-muted-foreground" title={cl.unit}>{cl.unit || '—'}</span>
                                  ) : (
                                    <input value={ln.unit ?? ''} disabled={readOnly} onChange={(e) => onChange(cat.name, ln.lineId, 'unit', e.target.value)} placeholder={cl.unit} className="grid-input w-16" />
                                  )}
                                </td>
                                <td className="px-1 py-1">
                                  {locked ? (
                                    <span data-testid={`cost-qty-locked-${ln.lineId}`}
                                      title={`Locked — ${ln.qtyPerUnit} per unit × sale qty ${qty} = ${+((Number(ln.qtyPerUnit) || 0) * qty).toFixed(2)}. Edit the composition in the Activity Master.`}
                                      className="flex w-12 items-center justify-end gap-1 py-1 pr-1 font-num text-xs text-muted-foreground">
                                      <Lock size={9} className="opacity-60" />{+((Number(ln.qtyPerUnit) || 0) * qty).toFixed(2)}
                                    </span>
                                  ) : (
                                    <NumberStepper value={ln.qtyPerUnit ?? 0} step={1} decimals={0} min={0} disabled={readOnly}
                                      data-testid={`cost-qty-${ln.lineId}`} inputClassName="w-10"
                                      onChange={(v) => onChange(cat.name, ln.lineId, 'qtyPerUnit', v)} />
                                  )}
                                </td>
                                <td className="px-1 py-1 text-right">
                                  {cl.linked ? (
                                    <span className="money block px-2 text-muted-foreground" title={cl.brandName ? `Brand rate: ${cl.brandName}` : 'Master rate'}>{money(cl.masterRate)}</span>
                                  ) : (
                                    <NumberStepper value={rawLn.rate ?? 0} step={1} decimals={2} min={0} disabled={readOnly}
                                      data-testid={`cost-rate-${ln.lineId}`} inputClassName="w-16"
                                      onChange={(v) => onChange(cat.name, ln.lineId, 'rate', v)} />
                                  )}
                                </td>
                                {isMat && (
                                  <KFactorCell
                                    lineId={ln.lineId}
                                    kValueRaw={rawLn.kValue}
                                    masterRateFils={cl.masterRate}
                                    disabled={readOnly}
                                    active={applyK}
                                    onChange={(v) => onChange(cat.name, ln.lineId, 'kValue', v === null ? '' : v)}
                                  />
                                )}
                                <td className={cn('money px-2 py-1 font-medium', isMat && applyK && cl.kValue !== cl.masterRate && 'text-[hsl(var(--copper))]')}>{money(cl.amount)}</td>
                                <td className="px-1 py-1">
                                  {!readOnly && !locked && (
                                    <div className="flex justify-end gap-0.5">
                                      <button onClick={() => onDuplicateLine(cat.name, ln.lineId)} className="rounded p-1 text-muted-foreground hover:bg-muted" title="Duplicate this line"><Copy size={12} /></button>
                                      <button onClick={() => onDeleteLine(cat.name, ln.lineId)} className="rounded p-1 text-muted-foreground hover:text-[hsl(var(--danger))]" title="Delete this line"><Trash2 size={12} /></button>
                                    </div>
                                  )}
                                  {!readOnly && locked && (
                                    <div className="flex justify-end" title="Locked by activity — detach the activity above to edit">
                                      <Lock size={12} className="text-muted-foreground opacity-50" />
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center gap-2 px-2 py-1.5">
                      {readOnly ? (
                        <span className="px-2 py-1 text-[11px] text-muted-foreground">Read-only — this tender is Won and frozen.</span>
                      ) : (
                        <span className="px-2 py-1 text-[11px] text-muted-foreground">Cost lines come from the Activity Master — use “Apply activity” above to add them.</span>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between border-t px-4 py-3">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Total Tender Price (Cost)</span>
        <span className="money text-sm font-bold" data-testid="total-tender-cost">{code} {money(projectDirectCost)}</span>
      </div>

      {/* Bulk delete confirm */}
      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent data-testid="cost-boq-bulk-delete-dialog">
          <DialogHeader><DialogTitle>Delete {selCount} line item{selCount > 1 ? 's' : ''}?</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">This removes the selected cost line(s). You can undo immediately from the toast that appears.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)}>Cancel</Button>
            <Button className="bg-[hsl(var(--danger))] text-white hover:brightness-95" data-testid="cost-boq-bulk-delete-confirm" onClick={doBulkDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pasteCat} onOpenChange={(o) => !o && setPasteCat(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Paste rows into {pasteCat}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Paste tab- or comma-separated rows: <span className="font-num">code, description, unit, qty/unit, rate</span></p>
          <Textarea rows={6} value={pasteText} onChange={(e) => setPasteText(e.target.value)} className="font-num text-xs"
            placeholder={'MAT-021\t\t\t10\nMISC\tApprovals\tlot\t1\t2500'} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasteCat(null)}>Cancel</Button>
            <Button onClick={handlePaste}>Add rows</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
